// Scans (store logins, warehouse app 0.8.0): what was scanned in a warehouse
// on a day or a few: items picked and checked, labels logged leaving with
// the app's Scanner or its carrier pickup check, by order or one by one,
// filtered by carrier, person and step; the packages each carrier still has
// to take that day; and a CSV of the scans.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BiArrowBack } from "react-icons/bi";
import {
  MdKeyboardArrowDown,
  MdOutlineFileDownload,
  MdQrCodeScanner,
  MdRefresh,
} from "react-icons/md";

import DetailsModal from "../../../../Warehouse/WarehouseOrders/Components/OrderDetails/DetailsModal";
import { useSnackBar } from "../../../../../hooks/useSnackBar";
import {
  fetchPickup,
  fetchPickupCarriers,
  fetchScanReport,
  fetchScanWarehouses,
} from "../../../../../services/orderScans.services";
import {
  STAGE_WORDS,
  ofText,
  resultTone,
  scanExtras,
  timeText,
} from "../../../../Warehouse/WarehouseOrders/Components/OrderDetails/OrderView/orderScansModel";
import {
  STAGE_OPTIONS,
  csvName,
  csvOf,
  dayText,
  groupByOrder,
  meaningText,
  missingByOrder,
  packageText,
  shiftDay,
  totalsOf,
} from "./scansModel";

const WAREHOUSE_KEY = "bolt.scans.warehouse";
const PAGE_SIZE = 500;
const MAX_PAGES = 10;
const SHOWN = 300;

const TONE_CLASS = {
  success: "bg-state-success-bg text-state-success-text",
  danger: "bg-state-danger-bg text-state-danger-text",
  warning: "bg-state-warning-bg text-state-warning-text",
  neutral: "bg-state-neutral-bg text-state-neutral-text",
};

const FIELD =
  "rounded-md border border-ui-border bg-ui-surface px-2 py-1 text-[12.5px] text-ui-text focus:border-brand focus:outline-none";
const BUTTON =
  "flex items-center gap-[5px] whitespace-nowrap rounded-md border border-ui-border bg-ui-surface px-[11px] py-[5px] text-xs font-semibold text-ui-text-muted transition-colors hover:border-brand hover:bg-brand-light hover:text-brand disabled:opacity-50";

const noop = () => {};

const errorText = (error) =>
  error?.response?.data?.message ||
  (error?.response?.status === 403
    ? "Scans are for store logins."
    : "Couldn't load the scans. Try again.");

const readSaved = () => {
  try {
    return Number(window.localStorage.getItem(WAREHOUSE_KEY)) || null;
  } catch {
    return null;
  }
};

const save = (id) => {
  try {
    window.localStorage.setItem(WAREHOUSE_KEY, String(id));
  } catch {
    // Not saved: the first warehouse comes back next time.
  }
};

function download(name, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const Chip = ({ tone = "neutral", children }) => (
  <span
    className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}
  >
    {children}
  </span>
);

const who = (row) =>
  [timeText(row?.at), row?.by?.name].filter(Boolean).join(" · ");

// The scans one by one: time, who, step, what it meant, order, package,
// carrier, note and code.
function ScanRows({ rows, onOpen, showOrder = true }) {
  return (
    <table className="w-full border-collapse text-[12px]">
      <thead>
        <tr className="text-left text-[11px] text-ui-text-muted">
          <th className="py-1 pr-2 font-medium">Time</th>
          <th className="py-1 pr-2 font-medium">Who</th>
          <th className="py-1 pr-2 font-medium">Step</th>
          <th className="py-1 pr-2 font-medium">What it meant</th>
          {showOrder && <th className="py-1 pr-2 font-medium">Order</th>}
          <th className="py-1 pr-2 font-medium">Package</th>
          <th className="py-1 pr-2 font-medium">Note</th>
          <th className="py-1 font-medium">Code</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-t border-ui-border align-top">
            <td className="whitespace-nowrap py-1 pr-2 text-ui-text-subtle">
              {timeText(row.at)}
            </td>
            <td className="py-1 pr-2 text-ui-text">{row.by?.name ?? "-"}</td>
            <td className="py-1 pr-2 text-ui-text-muted">
              {STAGE_WORDS[row.stage] ?? row.stage}
            </td>
            <td className="py-1 pr-2">
              <Chip tone={resultTone(row.result)}>{meaningText(row)}</Chip>
            </td>
            {showOrder && (
              <td className="py-1 pr-2">
                {row.order ? (
                  <button
                    type="button"
                    onClick={() => onOpen(row.order.id)}
                    className="font-medium text-brand hover:underline"
                  >
                    {row.order.order_number}
                  </button>
                ) : (
                  <span className="text-ui-text-subtle">-</span>
                )}
              </td>
            )}
            <td className="py-1 pr-2 text-ui-text-muted">
              {packageText(row) || "-"}
            </td>
            <td className="py-1 pr-2">
              <span className="flex flex-wrap gap-1">
                {scanExtras(row).map((extra) => (
                  <Chip
                    key={extra}
                    tone={extra === "scanned before" ? "warning" : "neutral"}
                  >
                    {extra}
                  </Chip>
                ))}
              </span>
            </td>
            <td className="py-1">
              <code className="break-all text-[11px] text-ui-text-subtle">
                {row.code}
              </code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// The scans by order: one row an order, opened to see its scans.
function OrderRows({ groups, onOpen }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (key) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return (
    <table className="w-full border-collapse text-[12.5px]">
      <thead>
        <tr className="text-left text-[11px] text-ui-text-muted">
          <th className="py-1.5 pr-2 font-medium">Order</th>
          <th className="py-1.5 pr-2 font-medium">Last scan</th>
          <th className="py-1.5 pr-2 font-medium">Items scanned</th>
          <th className="py-1.5 pr-2 font-medium">Labels</th>
          <th className="py-1.5 pr-2 font-medium">Carrier</th>
          <th className="py-1.5 pr-2 font-medium">Steps</th>
          <th className="py-1.5 pr-2 font-medium">Notes</th>
          <th className="py-1.5 font-medium" aria-label="Open" />
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => {
          const isOpen = expanded.has(group.key);
          return (
            <React.Fragment key={group.key}>
              <tr className="border-t border-ui-border align-top hover:bg-ui-row-hover">
                <td className="py-1.5 pr-2">
                  <button
                    type="button"
                    onClick={() => toggle(group.key)}
                    aria-expanded={isOpen}
                    className="flex items-center gap-1 text-left font-semibold text-ui-text"
                  >
                    <MdKeyboardArrowDown
                      size={16}
                      className={`shrink-0 text-ui-text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                    {group.order
                      ? group.order.order_number
                      : "Codes not in Bolt"}
                    <span className="font-normal text-ui-text-subtle">
                      ({group.scans.length})
                    </span>
                  </button>
                  <span className="mt-0.5 flex flex-wrap gap-1 pl-5">
                    {group.problems > 0 && group.order ? (
                      <Chip tone="danger">
                        {group.problems === 1
                          ? "1 wrong scan"
                          : `${group.problems} wrong scans`}
                      </Chip>
                    ) : null}
                    {group.duplicates > 0 ? (
                      <Chip tone="warning">
                        {group.duplicates} scanned before
                      </Chip>
                    ) : null}
                  </span>
                </td>
                <td className="py-1.5 pr-2 text-ui-text-muted">
                  {who(group.last)}
                </td>
                <td className="py-1.5 pr-2 text-ui-text">
                  {group.items || "-"}
                </td>
                <td className="py-1.5 pr-2">
                  {group.labels ? (
                    <Chip
                      tone={
                        group.packages && group.labels >= group.packages
                          ? "success"
                          : "warning"
                      }
                    >
                      {ofText(group.labels, group.packages)}
                    </Chip>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="py-1.5 pr-2 text-ui-text">
                  {group.carriers.join(", ") || "-"}
                </td>
                <td className="py-1.5 pr-2 text-ui-text-muted">
                  {group.stages
                    .map((stage) => STAGE_WORDS[stage] ?? stage)
                    .join(", ")}
                </td>
                <td className="py-1.5 pr-2 text-ui-text-muted">
                  {group.notes.join(", ") || "-"}
                </td>
                <td className="py-1.5 text-right">
                  {group.order ? (
                    <button
                      type="button"
                      onClick={() => onOpen(group.order.id)}
                      className="text-[12px] font-semibold text-brand hover:underline"
                    >
                      Open
                    </button>
                  ) : null}
                </td>
              </tr>
              {isOpen && (
                <tr className="bg-ui-surface-sunken">
                  <td colSpan={8} className="px-3 py-2">
                    <ScanRows
                      rows={group.scans}
                      onOpen={onOpen}
                      showOrder={false}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// The packages each carrier should take on the day, and the ones not
// scanned leaving yet.
function PickupPanel({ warehouseId, day, carrierFilter, reloadKey, onOpen }) {
  const [carriers, setCarriers] = useState({ loading: true, list: [] });
  const [chosen, setChosen] = useState(null);
  const [pickup, setPickup] = useState({ loading: false, list: null });
  const carrier = chosen ?? (carrierFilter || null);

  useEffect(() => {
    setChosen(null);
  }, [day, warehouseId]);

  useEffect(() => {
    if (!warehouseId || !day) return undefined;
    let active = true;
    setCarriers((current) => ({ ...current, loading: true }));
    fetchPickupCarriers(warehouseId, day).then(({ data, error }) => {
      if (active)
        setCarriers({
          loading: false,
          list: error ? [] : data?.result?.carriers || [],
          error,
        });
    });
    return () => {
      active = false;
    };
  }, [warehouseId, day, reloadKey]);

  useEffect(() => {
    if (!warehouseId || !day || carrier === null) return undefined;
    let active = true;
    setPickup({ loading: true, list: null });
    fetchPickup(warehouseId, day, carrier).then(({ data, error }) => {
      if (active)
        setPickup({ loading: false, list: error ? null : data?.result, error });
    });
    return () => {
      active = false;
    };
  }, [warehouseId, day, carrier, reloadKey]);

  if (!day) {
    return (
      <p className="m-0 text-[12px] text-ui-text-subtle">
        Choose one day to see which packages each carrier still has to take.
      </p>
    );
  }
  const missing = pickup.list ? missingByOrder(pickup.list) : [];
  const counts = pickup.list?.counts;
  const chip = (value, label) => {
    const active = carrier === value;
    return (
      <button
        key={value || "all"}
        type="button"
        onClick={() => setChosen(active ? null : value)}
        aria-pressed={active}
        className={`rounded-md border px-2 py-1 text-[12px] font-medium transition-colors ${
          active
            ? "border-brand bg-brand text-white"
            : "border-ui-border bg-ui-surface text-ui-text hover:border-brand"
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {carriers.loading ? (
        <p className="m-0 text-[12px] text-ui-text-subtle">Loading…</p>
      ) : carriers.error ? (
        <p className="m-0 text-[12px] text-state-danger-text">
          {errorText(carriers.error)}
        </p>
      ) : carriers.list.length === 0 ? (
        <p className="m-0 text-[12px] text-ui-text-subtle">
          No packages to take this day: no labels were made, and no open order
          has one waiting.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {carriers.list.map((item) =>
            chip(
              item.carrier,
              `${item.carrier} · ${ofText(item.scanned || 0, item.expected)}`,
            ),
          )}
          {carriers.list.length > 1 ? chip("", "All carriers") : null}
        </div>
      )}
      {carrier !== null && (
        <div className="rounded-md border border-ui-border p-2">
          {pickup.loading ? (
            <p className="m-0 text-[12px] text-ui-text-subtle">Loading…</p>
          ) : pickup.error ? (
            <p className="m-0 text-[12px] text-state-danger-text">
              {errorText(pickup.error)}
            </p>
          ) : counts ? (
            <>
              <p className="m-0 text-[12.5px] font-semibold text-ui-text">
                {carrier || "All carriers"}: {counts.scanned} of{" "}
                {counts.expected} scanned leaving
                {counts.missing ? (
                  <span className="ml-1 font-normal text-state-warning-text">
                    · {counts.missing} missing
                  </span>
                ) : (
                  <span className="ml-1 font-normal text-state-success-text">
                    · nothing missing
                  </span>
                )}
              </p>
              {missing.length > 0 && (
                <ul className="m-0 mt-1 list-none p-0">
                  {missing.map((group) => (
                    <li
                      key={group.order.id}
                      className="border-t border-ui-border py-1 text-[12px]"
                    >
                      <button
                        type="button"
                        onClick={() => onOpen(group.order.id)}
                        className="font-semibold text-brand hover:underline"
                      >
                        {group.order.order_number}
                      </button>
                      {group.order.customer ? (
                        <span className="ml-1 text-ui-text-muted">
                          {group.order.customer}
                        </span>
                      ) : null}
                      <span className="ml-1 text-ui-text">
                        ·{" "}
                        {group.packages
                          .map(
                            (item) =>
                              `${carrier ? "" : `${item.carrier} `}package ${item.package_no} of ${item.packages}`,
                          )
                          .join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function Scans() {
  const notify = useSnackBar();
  const today = dayText();
  const [warehouses, setWarehouses] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [filters, setFilters] = useState({
    from: today,
    to: today,
    carrier: "",
    userId: "",
    stage: "",
    order: "",
  });
  const [orderText, setOrderText] = useState("");
  const [view, setView] = useState("orders");
  const [shown, setShown] = useState(SHOWN);
  const [reloadKey, setReloadKey] = useState(0);
  const [detail, setDetail] = useState(false);
  const [report, setReport] = useState({
    loading: true,
    error: null,
    scans: [],
    carriers: [],
    users: [],
    counts: [],
    total: 0,
    capped: false,
  });

  useEffect(() => {
    let active = true;
    fetchScanWarehouses().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setWarehouses([]);
        setReport((current) => ({ ...current, loading: false, error }));
        return;
      }
      // Warehouses with the account's orders first. The others stay: a user
      // who may only view orders sees scans too (Bolt says if not).
      const all = Array.isArray(data?.result) ? data.result : [];
      const list = [
        ...all.filter((warehouse) => warehouse?.can?.orders),
        ...all.filter((warehouse) => !warehouse?.can?.orders),
      ];
      setWarehouses(list);
      const saved = readSaved();
      const first = list.find((warehouse) => warehouse.id === saved) || list[0];
      setWarehouseId(first ? first.id : null);
      if (!first) setReport((current) => ({ ...current, loading: false }));
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!warehouseId) return undefined;
    let active = true;
    setReport((current) => ({ ...current, loading: true, error: null }));
    setShown(SHOWN);
    (async () => {
      const params = {
        warehouse_id: warehouseId,
        date_from: filters.from,
        date_to: filters.to,
        carrier: filters.carrier,
        user_id: filters.userId,
        stage: filters.stage,
        order: filters.order,
        per_page: PAGE_SIZE,
      };
      const seen = new Set();
      const scans = [];
      let first = null;
      let last = 1;
      for (let page = 1; page <= Math.min(last, MAX_PAGES); page += 1) {
        // Pages one after another: new scans arriving meanwhile only push
        // rows to the next page, so a row may come twice (kept once).
        // eslint-disable-next-line no-await-in-loop
        const { data, error } = await fetchScanReport({ ...params, page });
        if (!active) return;
        if (error) {
          setReport((current) => ({ ...current, loading: false, error }));
          return;
        }
        const result = data?.result || {};
        if (!first) first = result;
        (result.scans || []).forEach((row) => {
          if (row?.id && !seen.has(row.id)) {
            seen.add(row.id);
            scans.push(row);
          }
        });
        last = Number(result.last_page) || 1;
      }
      setReport({
        loading: false,
        error: null,
        scans,
        carriers: first?.carriers || [],
        users: first?.users || [],
        counts: first?.counts || [],
        total: Number(first?.total) || scans.length,
        capped: last > MAX_PAGES,
        from: first?.from || null,
      });
    })();
    return () => {
      active = false;
    };
  }, [warehouseId, filters, reloadKey]);

  const groups = useMemo(() => groupByOrder(report.scans), [report.scans]);
  const totals = useMemo(() => totalsOf(report.counts), [report.counts]);
  const warehouse = (warehouses || []).find((item) => item.id === warehouseId);
  const day = filters.from === filters.to ? filters.from : null;
  const set = (changes) =>
    setFilters((current) => ({ ...current, ...changes }));
  const setDays = (from, to) =>
    set(from <= to ? { from, to } : { from: to, to: from });
  const open = (orderId) => setDetail([orderId, false]);

  const exportCsv = () => {
    if (!report.scans.length) return;
    download(csvName(warehouse, filters.from, filters.to), csvOf(report.scans));
    if (report.capped)
      notify({
        open: true,
        severity: "warning",
        text: `Only the newest ${report.scans.length.toLocaleString()} of ${report.total.toLocaleString()} scans are in the file. Choose fewer days or a filter for the rest.`,
      });
  };

  return (
    <div className="min-h-screen px-3 py-4 md:px-6">
      <DetailsModal
        open={detail}
        setOpen={setDetail}
        setSnack={notify}
        setOrders={noop}
        editable={false}
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Link
            to="/orders"
            aria-label="Back to orders"
            className="text-ui-text-muted hover:text-brand"
          >
            <BiArrowBack size={20} />
          </Link>
          <MdQrCodeScanner size={20} className="text-ui-text" />
          <h1 className="m-0 text-[18px] font-semibold text-ui-text">Scans</h1>
          {warehouses && warehouses.length > 1 ? (
            <select
              aria-label="Warehouse"
              value={warehouseId ?? ""}
              onChange={(event) => {
                const id = Number(event.target.value);
                setWarehouseId(id);
                save(id);
              }}
              className={FIELD}
            >
              {warehouses.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          ) : warehouse ? (
            <span className="text-[13px] text-ui-text-muted">
              {warehouse.name}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-[7px]">
          <button
            type="button"
            onClick={() => setReloadKey((key) => key + 1)}
            className={BUTTON}
            disabled={!warehouseId}
          >
            <MdRefresh className="h-3.5 w-3.5" />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className={BUTTON}
            disabled={!report.scans.length}
          >
            <MdOutlineFileDownload className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>
      <p className="m-0 mb-3 text-[12.5px] text-ui-text-muted">
        From the HAJEX BOLT warehouse app: items scanned while picking and
        checking, and shipping labels logged leaving with its Scanner and
        carrier pickup check.
      </p>

      {warehouses && warehouses.length === 0 && !report.error ? (
        <div className="rounded-lg border border-ui-border bg-ui-surface p-4 text-[13px] text-ui-text-muted">
          None of your orders are in a warehouse that uses the warehouse app
          yet.
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-ui-border bg-ui-surface p-2">
            <label className="flex flex-col gap-0.5 text-[11px] text-ui-text-muted">
              From
              <input
                type="date"
                value={filters.from}
                max={today}
                onChange={(event) =>
                  event.target.value && setDays(event.target.value, filters.to)
                }
                className={FIELD}
              />
            </label>
            <label className="flex flex-col gap-0.5 text-[11px] text-ui-text-muted">
              To
              <input
                type="date"
                value={filters.to}
                max={today}
                onChange={(event) =>
                  event.target.value &&
                  setDays(filters.from, event.target.value)
                }
                className={FIELD}
              />
            </label>
            <div className="flex gap-1">
              <button
                type="button"
                className={BUTTON}
                onClick={() => setDays(today, today)}
              >
                Today
              </button>
              <button
                type="button"
                className={BUTTON}
                onClick={() => {
                  const yesterday = shiftDay(today, -1);
                  setDays(yesterday, yesterday);
                }}
              >
                Yesterday
              </button>
              <button
                type="button"
                className={BUTTON}
                onClick={() => setDays(shiftDay(today, -6), today)}
              >
                Last 7 days
              </button>
            </div>
            <label className="flex flex-col gap-0.5 text-[11px] text-ui-text-muted">
              Carrier
              <select
                value={filters.carrier}
                onChange={(event) => set({ carrier: event.target.value })}
                className={FIELD}
              >
                <option value="">Every carrier</option>
                {report.carriers.map((carrier) => (
                  <option key={carrier} value={carrier}>
                    {carrier}
                  </option>
                ))}
                {filters.carrier &&
                !report.carriers.includes(filters.carrier) ? (
                  <option value={filters.carrier}>{filters.carrier}</option>
                ) : null}
              </select>
            </label>
            <label className="flex flex-col gap-0.5 text-[11px] text-ui-text-muted">
              Who
              <select
                value={filters.userId}
                onChange={(event) => set({ userId: event.target.value })}
                className={FIELD}
              >
                <option value="">Everyone</option>
                {report.users.map((person) => (
                  <option key={person.id} value={String(person.id)}>
                    {person.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-0.5 text-[11px] text-ui-text-muted">
              Step
              <select
                value={filters.stage}
                onChange={(event) => set({ stage: event.target.value })}
                className={FIELD}
              >
                {STAGE_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <form
              className="flex flex-col gap-0.5 text-[11px] text-ui-text-muted"
              onSubmit={(event) => {
                event.preventDefault();
                set({ order: orderText.trim() });
              }}
            >
              <label htmlFor="scans-order">Order</label>
              <input
                id="scans-order"
                type="search"
                value={orderText}
                placeholder="Order number"
                onChange={(event) => {
                  setOrderText(event.target.value);
                  if (!event.target.value) set({ order: "" });
                }}
                onBlur={() => set({ order: orderText.trim() })}
                className={FIELD}
              />
            </form>
          </div>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
            <section className="min-w-0 flex-1 overflow-hidden rounded-lg border border-ui-border bg-ui-surface">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ui-border px-3 py-2">
                <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-ui-text">
                  <span className="font-semibold">
                    {report.loading
                      ? "Loading…"
                      : `${totals.scans.toLocaleString()} ${totals.scans === 1 ? "scan" : "scans"}`}
                  </span>
                  {!report.loading && totals.scans > 0 ? (
                    <>
                      <Chip tone="success">{totals.items} items</Chip>
                      <Chip tone="success">{totals.labels} labels</Chip>
                      {totals.problems > 0 ? (
                        <Chip tone="danger">{totals.problems} wrong</Chip>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <div
                  className="flex overflow-hidden rounded-md border border-ui-border"
                  role="group"
                  aria-label="Show"
                >
                  {[
                    ["orders", "By order"],
                    ["scans", "Every scan"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={view === value}
                      onClick={() => setView(value)}
                      className={`px-2.5 py-1 text-[12px] font-medium ${
                        view === value
                          ? "bg-brand text-white"
                          : "bg-ui-surface text-ui-text-muted hover:text-brand"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto px-3 py-2">
                {report.error ? (
                  <p className="m-0 py-2 text-[12.5px] text-state-danger-text">
                    {errorText(report.error)}
                  </p>
                ) : report.loading ? null : report.scans.length === 0 ? (
                  <p className="m-0 py-2 text-[12.5px] text-ui-text-subtle">
                    Nothing scanned on these days with these filters.
                  </p>
                ) : view === "orders" ? (
                  <OrderRows groups={groups} onOpen={open} />
                ) : (
                  <>
                    <ScanRows
                      rows={report.scans.slice(0, shown)}
                      onOpen={open}
                    />
                    {report.scans.length > shown && (
                      <button
                        type="button"
                        onClick={() => setShown((count) => count + SHOWN)}
                        className="mt-1 text-[12px] font-medium text-brand hover:underline"
                      >
                        Show {Math.min(SHOWN, report.scans.length - shown)} more
                      </button>
                    )}
                  </>
                )}
                {!report.loading &&
                report.from &&
                report.from > filters.from ? (
                  <p className="m-0 mt-2 text-[12px] text-state-warning-text">
                    92 days at a time: showing the scans from {report.from}.
                  </p>
                ) : null}
                {report.capped && !report.loading ? (
                  <p className="m-0 mt-2 text-[12px] text-state-warning-text">
                    Showing the newest {report.scans.length.toLocaleString()} of{" "}
                    {report.total.toLocaleString()} scans. Choose fewer days or
                    a filter to see the rest.
                  </p>
                ) : null}
              </div>
            </section>

            <section className="w-full shrink-0 rounded-lg border border-ui-border bg-ui-surface px-3 py-2 xl:w-[360px]">
              <h2 className="m-0 mb-2 text-[13px] font-semibold text-ui-text">
                Carrier pickup{day ? ` · ${day}` : ""}
              </h2>
              <PickupPanel
                warehouseId={warehouseId}
                day={day}
                carrierFilter={filters.carrier}
                reloadKey={reloadKey}
                onOpen={open}
              />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
